import { http } from '@/api/http'
import type { Product } from '@/lib/pos-types'
import { normalizeProduct, unwrapList, unwrapSingle } from '@/api/normalizers'

export const productsApi = {
  list: async (): Promise<Product[]> => {
    const res = await http.get<unknown>('/products')
    return unwrapList(res.data).map(normalizeProduct)
  },

  create: async (payload: {
    name: string
    description?: string
    price: number
    categoryId: string
    stationKey?: string | null
    available?: boolean
    preparationTime?: number | null
    imageFile?: File | null
    availabilityRules?: { days: number[]; start: string; end: string }[]
    priceRules?: { days: number[]; start: string; end: string; price: number }[]
  }): Promise<Product> => {
    const formData = new FormData()
    formData.append('name', payload.name)
    formData.append('price', payload.price.toString())
    formData.append('category_id', payload.categoryId)
    if (payload.stationKey !== undefined) formData.append('station_key', payload.stationKey ?? '')
    if (payload.description) formData.append('description', payload.description)
    if (payload.available !== undefined) formData.append('available', payload.available ? '1' : '0')
    if (payload.preparationTime) formData.append('preparation_time', payload.preparationTime.toString())
    if (payload.imageFile) formData.append('image_file', payload.imageFile)
    if (payload.availabilityRules) formData.append('availability_rules', JSON.stringify(payload.availabilityRules))
    if (payload.priceRules) formData.append('price_rules', JSON.stringify(payload.priceRules))

    const res = await http.post<unknown>('/products', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    return normalizeProduct(unwrapSingle(res.data))
  },

  update: async (productId: string, payload: Record<string, unknown> & { imageFile?: File | null }): Promise<Product> => {
    // If we have an image file, use FormData + POST fallback for PUT/PATCH
    if (payload.imageFile) {
        const formData = new FormData()
        formData.append('_method', 'PATCH') // Tell Laravel it's a PATCH request
        
        if (payload.name) formData.append('name', payload.name as string)
        if (payload.price) formData.append('price', payload.price.toString())
        if (payload.categoryId) formData.append('category_id', payload.categoryId as string)
        if (payload.stationKey !== undefined) formData.append('station_key', (payload.stationKey as string) ?? '')
        if (payload.description) formData.append('description', payload.description as string)
        if (payload.available !== undefined) formData.append('available', payload.available ? '1' : '0')
        if (payload.preparationTime) formData.append('preparation_time', payload.preparationTime.toString())
        if (payload.availabilityRules) formData.append('availability_rules', JSON.stringify(payload.availabilityRules))
        if (payload.priceRules) formData.append('price_rules', JSON.stringify(payload.priceRules))
        formData.append('image_file', payload.imageFile)

        const res = await http.post<unknown>(`/products/${productId}`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        })
        return normalizeProduct(unwrapSingle(res.data))
    }

    // regular json request
    const data = { ...payload }
    if ('imageFile' in data) delete data.imageFile
    if ('stationKey' in data) {
      // Leave as stationKey, backend accepts both stationKey and station_key
    }
    if ('availabilityRules' in data) {
      data.availability_rules = data.availabilityRules
      delete data.availabilityRules
    }
    if ('priceRules' in data) {
      data.price_rules = data.priceRules
      delete data.priceRules
    }
    const res = await http.patch<unknown>(`/products/${productId}`, data)
    return normalizeProduct(unwrapSingle(res.data))
  },

  delete: async (productId: string): Promise<void> => {
    await http.delete(`/products/${productId}`)
  },
}
